import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, MailOpen, Trash2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/api";
import { formatDateTime } from "@/constants";

export default function AdminMessages() {
  const [messages, setMessages] = useState([]);

  const load = () => api.get("/admin/messages").then((r) => setMessages(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const toggleRead = async (m) => {
    try {
      await api.patch(`/admin/messages/${m.id}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Supprimer le message de ${m.name} ?`)) return;
    try {
      await api.delete(`/admin/messages/${m.id}`);
      toast.success("Message supprimé");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-messages-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" data-testid="admin-messages-title">Messages reçus</h1>
        <p className="text-sm text-slate-500 mt-1">Messages envoyés via le formulaire de contact du site.</p>
      </div>

      {messages.length === 0 ? (
        <p className="text-sm text-slate-500 bg-white rounded-xl border border-slate-200 p-6" data-testid="messages-empty">
          Aucun message pour le moment.
        </p>
      ) : (
        <div className="space-y-4">
          {messages.map((m) => (
            <Card key={m.id} className={`border-slate-200 ${!m.read ? "border-l-4 border-l-sky-600" : "opacity-75"}`} data-testid={`message-card-${m.id}`}>
              <CardContent className="p-5 space-y-3">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-sky-100 flex items-center justify-center shrink-0">
                    {m.read ? <MailOpen className="h-4 w-4 text-sky-700" /> : <Mail className="h-4 w-4 text-sky-700" />}
                  </div>
                  <div className="flex-1 min-w-[180px]">
                    <p className="font-semibold text-slate-800">{m.name} <span className="font-normal text-slate-500">({m.email})</span></p>
                    <p className="text-xs text-slate-500">{formatDateTime(m.created_at)}</p>
                  </div>
                  {!m.read && (
                    <Badge className="bg-sky-100 text-sky-800 border border-sky-200 hover:bg-sky-100">Non lu</Badge>
                  )}
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => toggleRead(m)} data-testid={`message-toggle-${m.id}`}>
                      {m.read ? "Marquer non lu" : "Marquer lu"}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => remove(m)} data-testid={`message-delete-${m.id}`}>
                      <Trash2 className="h-3.5 w-3.5 text-red-600" />
                    </Button>
                  </div>
                </div>
                <div className="pl-13 sm:pl-14">
                  <p className="text-sm font-semibold text-slate-800">{m.subject}</p>
                  <p className="text-sm text-slate-600 whitespace-pre-line mt-1">{m.message}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
